/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useMemo } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import { FadeIn, StaggeredEntrance } from '@atlaskit/motion';
import { Stack } from '@atlaskit/primitives/compiled';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { type StoreFollowUp } from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { FollowUpButton } from '@atlassian/conversation-assistant-ui-components/ui/follow-up-button';

import { CREATE_CONFLUENCE_PAGE_FOLLOWUP_PROMPT } from '../../../controllers/chat-store/CREATE_CONFLUENCE_PAGE_FOLLOWUP_PROMPT';
import { CREATE_CONFLUENCE_PAGE_FOLLOWUP_TEXT } from '../../../controllers/chat-store/CREATE_CONFLUENCE_PAGE_FOLLOWUP_TEXT';

import messages from './messages';

const styles = cssMap({
	queryContainer: {
		marginTop: token('space.300'),
	},
});

type StoreFollowUpViewProps = {
	followUp: StoreFollowUp;
	onFollowUpClick: (prompt: string) => void;
};

export const StoreFollowUpView = ({
	followUp,
	onFollowUpClick,
}: StoreFollowUpViewProps): JSX.Element | null => {
	const followUps = followUp.content.message.follow_ups;
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();

	const isCreatePageExperimentEnabled = expValEquals(
		'confluence_fe_create_page_suggestion',
		'isEnabled',
		true,
	);

	const hasCreatePageSuggestion = useMemo(
		() => followUps?.some((f) => f.text === CREATE_CONFLUENCE_PAGE_FOLLOWUP_TEXT) ?? false,
		[followUps],
	);

	useEffect(() => {
		if (isCreatePageExperimentEnabled && hasCreatePageSuggestion) {
			sendAnalyticsEvent({
				eventType: 'screen',
				name: 'createConfluencePageSuggestion',
			});
		}
	}, [isCreatePageExperimentEnabled, hasCreatePageSuggestion, sendAnalyticsEvent]);

	if (!followUps || followUps.length === 0) {
		return null;
	}

	return (
		<StaggeredEntrance columns={1}>
			<Stack space="space.100" xcss={styles.queryContainer}>
				{followUps.map((followUp, index) => {
					const isSyntheticCreatePage = followUp.text === CREATE_CONFLUENCE_PAGE_FOLLOWUP_TEXT;
					const displayText = isSyntheticCreatePage
						? formatMessage(messages.createConfluencePageFollowUp)
						: followUp.text;

					return (
						<FadeIn key={index} entranceDirection="bottom" duration="large">
							{(props) => (
								<div {...props}>
									<FollowUpButton
										{...props}
										followUpIndex={index}
										followUpType={followUp.type}
										followUpText={displayText}
										onClick={() => {
											if (isCreatePageExperimentEnabled && isSyntheticCreatePage) {
												sendAnalyticsEvent({
													action: 'clicked',
													actionSubject: 'button',
													actionSubjectId: 'createConfluencePageSuggestion',
												});
											}
											// Send the verbatim-reproduction prompt, not the user-facing label.
											onFollowUpClick(
												isSyntheticCreatePage
													? CREATE_CONFLUENCE_PAGE_FOLLOWUP_PROMPT
													: followUp.text,
											);
										}}
									/>
								</div>
							)}
						</FadeIn>
					);
				})}
			</Stack>
		</StaggeredEntrance>
	);
};
